import React, { Fragment, useEffect, useState } from "react";
import {
  Row,
  Form,
  Button,
  InputGroup,
  ToastContainer,
  Toast,
  Col,
} from "react-bootstrap";
import Select from "react-select";
import { CurrencyRupeeIcon } from "@heroicons/react/outline";
import { RcsRateTable } from "components/Tables";
import {
  useUserDropDownMutation
} from "Services/services";
import hasPermission from "pages/Permissions";
import { useAssignRcsRouteToEnterpriseMutation, useGetRcsOnboardedBSPMutation } from "Services/rcsServices";

export default () => {
  const userData = localStorage.getItem("TOKEN");
  const userObject = JSON.parse(userData);
  const createdBy = userObject?.id;

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");

  const [refreshTable, setRefreshTable] = useState(false);

  const [enterpriseOptions, setEnterpriseOptions] = useState([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);

  const [routeOptions, setRouteOptions] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);

  const [routeName, setRouteName] = useState("");
  const [standardRate, setStandardRate] = useState("");
  const [richCardRate, setRichCardRate] = useState("");
  const [carouselRate, setCarouselRate] = useState("");

  const [enterpriseUserOptions, setEnterpriseUserOptions] = useState([]);
  const [selectedEnterpriseUsers, setSelectedEnterpriseUsers] = useState([]);

  const [userList] = useUserDropDownMutation();
  const [RoutesList] = useGetRcsOnboardedBSPMutation();
  const [assignRcsRoute] = useAssignRcsRouteToEnterpriseMutation();

  const canEdit = hasPermission("WA Rates", "edit");

  const [validated, setValidated] = useState(false);
  const [selectErrors, setSelectErrors] = useState({
    enterprise: false,
    route: false,
    routeName: false,
    standardRate: false,
    richCardRate: false,
    carouselRate: false,
    enterpriseUsers: false,
  });

  const refreshAll = () => {
    setSelectedEnterprise(null);
    setSelectedRoute(null);
    setStandardRate("");
    setRichCardRate("");
    setCarouselRate("");
    setSelectedEnterpriseUsers([]);
    setValidated(false);
    setSelectErrors({
      enterprise: false,
      route: false,
      routeName: false,
      standardRate: false,
      richCardRate: false,
      carouselRate: false,
      enterpriseUsers: false,
    });
  };

  useEffect(() => {
    fetchEnterprises();
    fetchEnterpriseUsers();
    fetchRoutes();
  }, []);

  const fetchEnterprises = async () => {
    try {
      const response = await userList({ user_type: 4 });
      if (response?.data?.statusCode === 200) {
        const options = response?.data?.data?.user_details.map((option) => ({
          value: option.id,
          label: option.user_name,
        }));
        setEnterpriseOptions(options);
      } else {
        setEnterpriseOptions([]);
      }
    } catch (error) {
      setEnterpriseOptions([]);
    }
  };

  const fetchRoutes = async () => {
    try {
      const response = await RoutesList({
        page: 1,
        limit: 20,
        search: ""
      });

      if (response?.data?.statusCode === 200) {
        const options = response?.data?.data?.items.map((option) => ({
          value: option.id,
          label: option.route_name,
        }));
        setRouteOptions(options);
      } else {
        setRouteOptions([]);
      }
    } catch (error) {
      setRouteOptions([]);
    }
  };

  const fetchEnterpriseUsers = async () => {
    try {
      const response = await userList({ user_type: 7 });
      if (response?.data?.statusCode === 200) {
        const options = response?.data?.data?.user_details.map((option) => ({
          value: option.id,
          label: option.user_name,
        }));
        setEnterpriseUserOptions(options);
      } else {
        setEnterpriseUserOptions([]);
      }
    } catch (error) {
      setEnterpriseUserOptions([]);
    }
  };

  const validateForm = () => {
    const errors = {
      enterprise: !selectedEnterprise,
      route: !selectedRoute,
      routeName: !routeName,
      standardRate: standardRate === "" || standardRate === "0",
      richCardRate: richCardRate === "" || richCardRate === "0",
      carouselRate: carouselRate === "" || carouselRate === "0",
      enterpriseUsers: !selectedEnterpriseUsers || selectedEnterpriseUsers.length === 0,
    };
    setSelectErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const isFormValid = validateForm();

    if (!isFormValid) {
      setValidated(true);
      return;
    }

    const payload = {
      associated_enterprise_id: selectedEnterprise.value,
      route_id: selectedRoute.value,
      created_by: createdBy,
      route_name: routeName,
      standard_rate: standardRate,
      rich_card_rate: richCardRate,
      carousel_rate: carouselRate,
      enterprise_users: selectedEnterpriseUsers.map((u) => u.value),
    };

    assignRcsRoute(payload)
      .then((success) => {
        if (success?.data?.statusCode >= 200 || success?.data?.statusCode <= 204) {
          setToastMessage(success?.data?.message || "Route assigned successfully");
          setToastVariant("success");
          setRefreshTable((prev) => !prev);
          refreshAll();
        } else {
          setToastMessage(success?.data?.message || "Assignment failed!");
          setToastVariant("danger");
        }
        setShowToast(true);
      })
      .catch((error) => {
        setToastMessage(error?.data?.message || "Assignment failed");
        setToastVariant("danger");
        setShowToast(true);
      });

    setValidated(true);
  };

  const RequiredLabel = ({ label }) => (
    <Form.Label>
      {label}
      <span className="text-danger"> * </span>
    </Form.Label>
  );

  return (
    <Fragment>
      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg={toastVariant}
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">RCS Route Assignment</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      {canEdit && (
        <Form border="0" className="card mb-4" noValidate validated={validated} autoComplete="off" onSubmit={handleSubmit}>
          <h5 className="mb-4">General information</h5>
          <Row>
            <Form.Group as={Col} xs={12} md={6} lg={4} className="mb-3">
              <RequiredLabel label="Enterprise" />
              <Select
                required
                placeholder="Select Enterprise"
                className="mb-0"
                isSearchable={true}
                options={enterpriseOptions}
                value={selectedEnterprise}
                onChange={(selectedOption) => {
                  setSelectedEnterprise(selectedOption);
                  setSelectErrors((prev) => ({
                    ...prev,
                    enterprise: false,
                  }));
                }}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderColor:
                      validated && selectErrors.enterprise
                        ? "#dc3545"
                        : validated && !selectErrors.enterprise && selectedEnterprise
                          ? "#65BB82"
                          : provided.borderColor,
                    borderRadius: "0.75rem",
                  }),
                }}
              />
              {validated && selectErrors.enterprise && (
                <div className="invalid-feedback d-block">
                  Enterprise selection is required.
                </div>
              )}
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} lg={4} className="mb-3">
              <RequiredLabel label="RCS BSP (Route)" />
              <Select
                required
                placeholder="Select RCS BSP"
                className="mb-0"
                isSearchable={true}
                options={routeOptions}
                value={selectedRoute}
                onChange={(selectedOption) => {
                  setSelectedRoute(selectedOption);
                  setSelectErrors((prev) => ({
                    ...prev,
                    route: false,
                  }));
                }}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderColor:
                      validated && selectErrors.route
                        ? "#dc3545"
                        : validated && !selectErrors.route && selectedRoute
                          ? "#65BB82"
                          : provided.borderColor,
                    borderRadius: "0.75rem",
                  }),
                }}
              />
              {validated && selectErrors.route && (
                <div className="invalid-feedback d-block">
                  RCS BSP selection is required.
                </div>
              )}
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} lg={4} className="mb-3">
              <RequiredLabel label="Route Name" />
              <InputGroup>
                <InputGroup.Text className="text-gray-600">
                  <CurrencyRupeeIcon className="icon icon-xs" />
                </InputGroup.Text>
                <Form.Control
                  required
                  type="text"
                  placeholder="route name"
                  value={routeName}
                  title="Route Name must be alphanumeric, max. 8 characters"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^[a-z0-9]{0,8}$/.test(val)) {
                      setRouteName(val);
                      setSelectErrors((prev) => ({
                        ...prev,
                        routeName: false,
                      }));
                    }
                  }}
                  isInvalid={validated && selectErrors.routeName}
                  isValid={
                    validated &&
                    !selectErrors.routeName &&
                    routeName &&
                    routeName !== "0"
                  }
                  autoComplete="new-routename"
                />
              </InputGroup>
              {validated && selectErrors.routeName && (
                <div className="invalid-feedback d-block">
                  Enter a valid Route Name.
                </div>
              )}
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} lg={4} className="mb-3">
              <RequiredLabel label="Standard Rate" />
              <InputGroup>
                <InputGroup.Text className="text-gray-600">
                  <CurrencyRupeeIcon className="icon icon-xs" />
                </InputGroup.Text>
                <Form.Control
                  required
                  type="text"
                  placeholder="e.g. 1.00000"
                  value={standardRate}
                  title="Standard rate must be a valid number"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d+(\.\d{0,5})?$/.test(val)) {
                      setStandardRate(val);
                      setSelectErrors((prev) => ({
                        ...prev,
                        standardRate: false,
                      }));
                    }
                  }}
                  isInvalid={validated && selectErrors.standardRate}
                  isValid={
                    validated &&
                    !selectErrors.standardRate &&
                    standardRate &&
                    standardRate !== "0"
                  }
                  autoComplete="new-standardRate"
                />
              </InputGroup>
              {validated && selectErrors.standardRate && (
                <div className="invalid-feedback d-block">
                  Enter a valid Standard Rate.
                </div>
              )}
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} lg={4} className="mb-3">
              <RequiredLabel label="Rich Card Rate" />
              <InputGroup>
                <InputGroup.Text className="text-gray-600">
                  <CurrencyRupeeIcon className="icon icon-xs" />
                </InputGroup.Text>
                <Form.Control
                  required
                  type="text"
                  placeholder="e.g. 1.00000"
                  value={richCardRate}
                  title="Rich Card rate must be a valid number"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d+(\.\d{0,5})?$/.test(val)) {
                      setRichCardRate(val);
                      setSelectErrors((prev) => ({
                        ...prev,
                        richCardRate: false,
                      }));
                    }
                  }}
                  isInvalid={validated && selectErrors.richCardRate}
                  isValid={
                    validated &&
                    !selectErrors.richCardRate &&
                    richCardRate &&
                    richCardRate !== "0"
                  }
                  autoComplete="new-richCardRate"
                />
              </InputGroup>
              {validated && selectErrors.richCardRate && (
                <div className="invalid-feedback d-block">
                  Enter a valid Rich Card Rate.
                </div>
              )}
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} lg={4} className="mb-3">
              <RequiredLabel label="Carousel Rate" />
              <InputGroup>
                <InputGroup.Text className="text-gray-600">
                  <CurrencyRupeeIcon className="icon icon-xs" />
                </InputGroup.Text>
                <Form.Control
                  required
                  type="text"
                  placeholder="e.g. 1.00000"
                  value={carouselRate}
                  title="Carousel rate must be a valid number"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d+(\.\d{0,5})?$/.test(val)) {
                      setCarouselRate(val);
                      setSelectErrors((prev) => ({
                        ...prev,
                        carouselRate: false,
                      }));
                    }
                  }}
                  isInvalid={validated && selectErrors.carouselRate}
                  isValid={
                    validated &&
                    !selectErrors.carouselRate &&
                    carouselRate &&
                    carouselRate !== "0"
                  }
                  autoComplete="new-carouselRate"
                />
              </InputGroup>
              {validated && selectErrors.carouselRate && (
                <div className="invalid-feedback d-block">
                  Enter a valid Carousel Rate.
                </div>
              )}
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} lg={4} className="mb-3">
              <RequiredLabel label="Enterprise Users" />
              <Select
                required
                placeholder="Select Enterprise Users"
                className="mb-0"
                isSearchable={true}
                isMulti
                options={enterpriseUserOptions}
                value={selectedEnterpriseUsers}
                onChange={(selectedOptions) => {
                  setSelectedEnterpriseUsers(selectedOptions || []);
                  setSelectErrors((prev) => ({
                    ...prev,
                    enterpriseUsers: false,
                  }));
                }}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderColor:
                      validated && selectErrors.enterpriseUsers
                        ? "#dc3545"
                        : validated &&
                          !selectErrors.enterpriseUsers &&
                          selectedEnterpriseUsers &&
                          selectedEnterpriseUsers.length > 0
                          ? "#65BB82"
                          : provided.borderColor,
                    borderRadius: "0.75rem",
                  }),
                }}
              />
              {validated && selectErrors.enterpriseUsers && (
                <div className="invalid-feedback d-block">
                  At least one enterprise user must be selected.
                </div>
              )}
            </Form.Group>

            <Col xs={12} className="d-flex justify-content-end">
              <Button
                variant="secondary"
                className="animate-up-2 mt-2"
                type="submit"
              >
                Submit
              </Button>
            </Col>
          </Row>
        </Form>
      )}

      <RcsRateTable key={refreshTable} />
    </Fragment>
  );
};
